import { useCallback, useEffect, useState } from 'react';

import { SubActivityEdit } from '../sub-activity/sub-activity-edit';
import { EditProps } from './draw.constants';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import {
  ComponentDimensions,
  useComponentDimensions,
} from '../shared/component-height/component-dimensions';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ComponentAltTextButton from '../shared/component-alt-text/component-alt-text-button';
import ComponentImageModal from '../shared/component-image-modal/component-image-modal';
import { ComponentActions } from '../components.style';
import { ImageStatic } from 'approot/shared/images/images';
import { useImageDimensionEffect } from 'approot/shared/images/images.hooks';
import * as Styled from './draw-edit.style';
import { CONTENT_IMAGE, LANGUAGE_CODE_EN_AU } from 'global/constants';
import { useFindComponentDimensions } from '../component.hooks';
import { COMPONENT_DIMENSION_ID_PREFIX } from '../shared/component-height/component-dimensions.constants';
import { DrawViewPreview } from './draw-view.style';
import DrawCanvasPreviewWrapper from './draw-canvas-preview';

const DrawEdit = (props: EditProps) => {
  const {
    id,
    sectionId,
    type,
    sort,
    properties,
    subActivityProperties,
    version,
  } = props;
  const [previewRef, setPreviewRef] = useState<HTMLDivElement | null>(null);

  const { activeComponent } = useActivityBuilderContext();
  const { loading, updateComponentProperties } =
    useBatchComponentUpdateContext();
  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const { width, height } = useFindComponentDimensions(
    id,
    COMPONENT_DIMENSION_ID_PREFIX
  );

  const { dimensions, setDimensions } = useComponentDimensions({
    defaultValue: properties.dimensionData,
  });

  const { imageDimensions, setContainerElement } = useImageDimensionEffect({
    imageUrl: properties.image?.original || '',
  });

  const handleHeightUpdate = useCallback(
    (width: number, height: number) => {
      const dimensionData = {
        ...properties.dimensionData,
        width,
        height: Math.floor(height),
      };
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: {
              dimensionData,
            },
          },
        },
        properties: {
          ...properties,
          dimensionData,
        },
        typename: 'ActivitiesDrawComponent',
      });
    },
    [id, sectionId, properties, updateComponentProperties]
  );

  useEffect(() => {
    if (
      dimensions.width !== properties.dimensionData.width ||
      dimensions.height !== properties.dimensionData.height
    ) {
      setDimensions(properties.dimensionData);
    }
    // eslint-disable-next-line
  }, [properties.dimensionData]);

  return (
    <SubActivityEdit
      id={id}
      component={{
        id,
        type,
        sort,
        properties,
        sectionId,
        language: LANGUAGE_CODE_EN_AU,
        version,
        __typename: 'ActivitiesDrawComponent',
      }}
      subActivityDetails={subActivityProperties}
    >
      {container => (
        <ComponentDimensions
          id={id}
          showHandle={activeComponent?.id === props.id}
          defaultValue={dimensions}
          onResize={(width, height) => {
            setDimensions({ width, height });
          }}
          onResizeEnd={handleHeightUpdate}
          isLoading={loading}
          extraHeight={container?.offsetHeight}
        >
          {isVisible && (
            <ComponentImageModal
              loading={loading}
              containerDimensions={{
                width: width,
                height: height,
              }}
              canFill={false}
              canFit={false}
              image={properties.image || undefined}
              altText={properties.altText}
              sourceUrl={properties.sourceUrl}
              closeModal={closeModal}
              onUpdateData={data => {
                updateComponentProperties({
                  input: {
                    sectionId,
                    componentId: id,
                    componentData: {
                      properties: {
                        altText: data.altText,
                        sourceUrl: data.sourceUrl,
                        image: data.image
                          ? {
                              fileName: data.image.fileName,
                              key: data.image.key,
                              imageType: CONTENT_IMAGE,
                              data: {
                                flip: data.image.data?.flip || null,
                              },
                            }
                          : null,
                      },
                    },
                  },
                  typename: 'ActivitiesDrawComponent',
                  isSaveImmediate: true,
                  onCompleted: () => closeModal(),
                });
              }}
            />
          )}
          <Styled.DrawEditImage ref={setContainerElement}>
            {properties.draw?.data ? (
              <DrawViewPreview
                ref={setPreviewRef}
                role="image"
                aria-label={properties.altText || ''}
              >
                {previewRef && (
                  <DrawCanvasPreviewWrapper
                    id={id}
                    key={`${id}-${properties.draw?.updatedAt}-${properties.image?.original}`}
                    dimensionsData={properties.dimensionData}
                    imageUrl={properties.image?.original}
                    language={LANGUAGE_CODE_EN_AU}
                    containerEl={previewRef}
                    answer={
                      properties.draw?.data
                        ? {
                            data: {
                              objects: properties.draw.data.objects,
                              version: properties.draw.data.version,
                            },
                            originalWidth: properties.draw.originalWidth || 0,
                            originalHeight: properties.draw.originalHeight || 0,
                          }
                        : undefined
                    }
                  />
                )}
              </DrawViewPreview>
            ) : (
              <ImageStatic
                imageDimensions={imageDimensions}
                imageUrl={properties.image?.original || ''}
                alt={properties.altText || ''}
              />
            )}
          </Styled.DrawEditImage>
          {properties.image?.original && (
            <ComponentActions>
              <ComponentAltTextButton
                hasAltText={!!properties.altText}
                onClick={openModal}
              />
            </ComponentActions>
          )}
        </ComponentDimensions>
      )}
    </SubActivityEdit>
  );
};

export default DrawEdit;
